import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    getCategoryLabel,
    getSubcategoryLabel
} from "../../utils/categoryLabels";
import { getRiskLevelColor } from "../../utils/riskLevelUtils";
import { levelDisplayNames } from "../../utils/displayNameUtils";

const FailedTestDetailsTab = ({ rawApiResponse }) => {
    const { t } = useTranslation("reports_recs", { keyPrefix: "reports_common" });
    const [failedTestsData, setFailedTestsData] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // Preprocess API response using the new structure:
    // score_json.data[category].failed_tests[subcategory] (expected to be an array)
    const preprocessApiResponse = (apiResponse) => {
        if (!apiResponse?.score_json?.data) return {};

        const processedData = {};
        const scoreData = apiResponse.score_json.data;

        Object.keys(scoreData).forEach(categoryKey => {
            const categoryData = scoreData[categoryKey];
            const failedTests = categoryData?.failed_tests || {};

            Object.keys(failedTests).forEach(subcategoryKey => {
                const testsArray = failedTests[subcategoryKey];

                // Only add subcategories that have failed tests arrays
                if (Array.isArray(testsArray) && testsArray.length > 0) {
                    if (!processedData[categoryKey]) processedData[categoryKey] = {};
                    processedData[categoryKey][subcategoryKey] = testsArray;
                }
            });
        });

        return processedData;
    };

    // Normalize to category -> subcategory -> array-of-tests
    // If `rawApiResponse` is already in that shape, we keep only array subvalues.
    const normalizeFailedTestsData = (apiResponse) => {
        // Preferred structure from backend
        if (apiResponse?.score_json?.data) {
            return preprocessApiResponse(apiResponse);
        }

        if (!apiResponse || typeof apiResponse !== "object") return {};

        const processedData = {};
        Object.keys(apiResponse).forEach(categoryKey => {
            const categoryValue = apiResponse[categoryKey];
            if (!categoryValue || typeof categoryValue !== "object") return;

            Object.keys(categoryValue).forEach(subcategoryKey => {
                const testsArray = categoryValue[subcategoryKey];
                if (Array.isArray(testsArray) && testsArray.length > 0) {
                    if (!processedData[categoryKey]) processedData[categoryKey] = {};
                    processedData[categoryKey][subcategoryKey] = testsArray;
                }
            });
        });

        return processedData;
    };

    // Process/normalize incoming data
    useEffect(() => {
        if (rawApiResponse && typeof rawApiResponse === 'object') {
            setIsProcessing(true);
            // Use setTimeout to allow UI to update and show spinner
            setTimeout(() => {
                const normalizedData = normalizeFailedTestsData(rawApiResponse);
                setFailedTestsData(normalizedData);
                setIsProcessing(false);
            }, 0);
        }
    }, [rawApiResponse]);

    // Set initial selected category and subcategory when data loads
    useEffect(() => {
        if (failedTestsData && Object.keys(failedTestsData).length > 0) {
            const validCategories = Object.keys(failedTestsData).filter(category =>
                failedTestsData[category] && typeof failedTestsData[category] === 'object' &&
                Object.keys(failedTestsData[category]).length > 0 &&
                !['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(category) // Not array indices
            );
            if (validCategories.length > 0 && (!selectedCategory || !validCategories.includes(selectedCategory))) {
                const newCategory = validCategories[0];
                setSelectedCategory(newCategory);

                // Set initial subcategory for the selected category
                const subcategories = Object.keys(failedTestsData[newCategory]);
                if (subcategories.length > 0) {
                    setSelectedSubcategory(subcategories[0]);
                }
            }
        }
    }, [failedTestsData, selectedCategory]);

    // Handle category change
    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        // Immediately set the first subcategory to avoid flickering
        if (failedTestsData && failedTestsData[category]) {
            const subcategories = Object.keys(failedTestsData[category]);
            if (subcategories.length > 0) {
                setSelectedSubcategory(subcategories[0]);
            }
        }
    };


    // Show loading state if no data or processing
    if (!failedTestsData || isProcessing) {
        return (
            <div style={{
                padding: "40px",
                textAlign: "center",
                color: "var(--body-text)"
            }}>
                <div style={{
                    width: "40px",
                    height: "40px",
                    border: "4px solid var(--card-border)",
                    borderTop: "4px solid #3b82f6",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 16px"
                }}></div>
                <div style={{ fontSize: "18px", marginBottom: "10px" }}>
                    {isProcessing ? t("processingTestData") : t("loadingFailedTestDetails")}
                </div>
                <div style={{ fontSize: "14px", color: "#666" }}>
                    {isProcessing ? t("analyzingSecurityTestResults") : t("preparingReportData")}
                </div>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    const validCategories = Object.keys(failedTestsData).filter(category =>
        failedTestsData[category] && typeof failedTestsData[category] === 'object' &&
        Object.keys(failedTestsData[category]).length > 0 &&
        !['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(category) // Not array indices
    );

    const selectedTestsArray = Array.isArray(
        failedTestsData?.[selectedCategory]?.[selectedSubcategory]
    )
        ? failedTestsData[selectedCategory][selectedSubcategory]
        : [];

    return (
        <div style={{ padding: "24px" }}>
                            {validCategories.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px", color: "var(--body-text)" }}>
                    <p>{t("allTestsBlockedSuccessfully")}</p>
                </div>
            ) : (
                <div>
                    {/* Category Button Selectors */}
                    <div style={{ marginBottom: "32px" }}>
                        <h2 style={{
                            color: "var(--card-text)",
                            marginBottom: "16px",
                            fontSize: "20px",
                            fontWeight: "600"
                        }}>
                            {t("securityCategories")}
                        </h2>
                        <div style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "12px",
                            marginBottom: "24px"
                        }}>
                            {validCategories.map(category => {
                                const totalFailedTests = Object.values(failedTestsData[category])
                                    .reduce((sum, tests) => sum + (Array.isArray(tests) ? tests.length : 0), 0);

                                return (
                                    <button
                                        key={category}
                                        onClick={() => handleCategoryChange(category)}
                                        style={{
                                            padding: "12px 20px",
                                            background: selectedCategory === category
                                                ? "linear-gradient(135deg, #3b82f6, #1d4ed8)"
                                                : "var(--card-bg)",
                                            color: selectedCategory === category ? "white" : "var(--body-text)",
                                            border: selectedCategory === category
                                                ? "2px solid #3b82f6"
                                                : "2px solid var(--card-border)",
                                            borderRadius: "8px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            transition: "all 0.3s ease",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            boxShadow: selectedCategory === category
                                                ? "0 4px 12px rgba(59, 130, 246, 0.3)"
                                                : "0 2px 4px rgba(0, 0, 0, 0.1)",
                                            minWidth: "200px",
                                            justifyContent: "space-between"
                                        }}
                                    >
                                        <span>{getCategoryLabel(category)}</span>
                                        <span style={{
                                            background: selectedCategory === category
                                                ? "rgba(255, 255, 255, 0.2)"
                                                : "#ef4444",
                                            color: selectedCategory === category ? "white" : "white",
                                            padding: "2px 8px",
                                            borderRadius: "12px",
                                            fontSize: "12px",
                                            fontWeight: "bold"
                                        }}>
                                            {totalFailedTests}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Subcategory Tabs */}
                    {selectedCategory && failedTestsData[selectedCategory] && (
                        <div>
                            {/* Subcategory Tab Navigation */}
                            <div style={{
                                display: "flex",
                                flexWrap: "nowrap",
                                gap: "0px",
                                borderBottom: "2px solid var(--card-border)",
                                marginBottom: "24px",
                                overflowX: "auto"
                            }}>
                                {Object.keys(failedTestsData[selectedCategory]).map(subcategory => (
                                    <button
                                        key={subcategory}
                                        onClick={() => setSelectedSubcategory(subcategory)}
                                        style={{
                                            padding: "12px 20px",
                                            background: selectedSubcategory === subcategory ? "var(--card-bg)" : "transparent",
                                            color: selectedSubcategory === subcategory ? "var(--body-text)" : "var(--body-text)",
                                            border: "none",
                                            borderBottom: selectedSubcategory === subcategory ? "3px solid #3b82f6" : "3px solid transparent",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            fontWeight: "400",
                                            transition: "all 0.2s ease",
                                            flex: "0 0 auto",
                                            textAlign: "center",
                                            borderRadius: "0px",
                                            whiteSpace: "nowrap",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            justifyContent: "center"
                                        }}
                                    >
                                        <span>{getSubcategoryLabel(selectedCategory, subcategory)}</span>
                                        <span style={{
                                            background: "#ef4444",
                                            color: "white",
                                            padding: "2px 6px",
                                            borderRadius: "10px",
                                            fontSize: "11px",
                                            fontWeight: "bold"
                                        }}>
                                            {Array.isArray(failedTestsData[selectedCategory]?.[subcategory])
                                                ? failedTestsData[selectedCategory][subcategory].length
                                                : 0}
                                        </span>
                                    </button>
                                ))}
                            </div>

                            {/* Selected Subcategory Content */}
                            {selectedSubcategory && Array.isArray(selectedTestsArray) && (
                                <div>
                                    <div style={{
                                        background: "var(--card-bg)",
                                        border: "1px solid var(--card-border)",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
                                    }}>
                                        <table style={{
                                            width: "100%",
                                            borderCollapse: "collapse",
                                            fontSize: "14px"
                                        }}>
                                            <thead>
                                                <tr style={{
                                                    background: "var(--card-border)",
                                                    color: "var(--card-text)"
                                                }}>
                                                    <th style={{
                                                        padding: "12px 20px",
                                                        textAlign: "left",
                                                        borderBottom: "1px solid var(--card-border)",
                                                        fontWeight: "600",
                                                        whiteSpace: "nowrap"
                                                    }}>
                                                        {t("id")}
                                                    </th>
                                                    <th style={{
                                                        padding: "12px 20px",
                                                        textAlign: "left",
                                                        borderBottom: "1px solid var(--card-border)",
                                                        fontWeight: "600"
                                                    }}>
                                                        {t("prompt")}
                                                    </th>
                                                    <th style={{
                                                        padding: "12px 20px",
                                                        textAlign: "center",
                                                        borderBottom: "1px solid var(--card-border)",
                                                        fontWeight: "600",
                                                        whiteSpace: "nowrap"
                                                    }}>
                                                        {t("responseTimeMs")}
                                                    </th>
                                                    <th style={{
                                                        padding: "12px 20px",
                                                        textAlign: "center",
                                                        borderBottom: "1px solid var(--card-border)",
                                                        fontWeight: "600",
                                                        whiteSpace: "nowrap"
                                                    }}>
                                                        {t("riskLevel")}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedTestsArray.map((test, index) => (
                                                    <tr key={index} style={{
                                                        borderBottom: index < selectedTestsArray.length - 1 ? "1px solid var(--card-border)" : "none"
                                                    }}>
                                                        <td style={{
                                                            padding: "16px 20px",
                                                            color: "var(--card-text)",
                                                            lineHeight: "1.4",
                                                            verticalAlign: "top",
                                                            fontWeight: "500",
                                                            whiteSpace: "nowrap"
                                                        }}>
                                                            {test?.test_id ?? t("na")}
                                                        </td>
                                                        <td style={{
                                                            padding: "16px 20px",
                                                            color: "var(--card-text)",
                                                            wordBreak: "break-word",
                                                            lineHeight: "1.4",
                                                            verticalAlign: "top",
                                                            whiteSpace: "pre-wrap"
                                                        }}>
                                                            {(test?.payload ?
                                                                (typeof test.payload === 'string' ? test.payload :
                                                                    (typeof test.payload === 'object' ? (test.payload.input_text || t("na")) : t("na")))
                                                                : t("na"))
                                                            + (
                                                                test?.response?.response ?
                                                                `\n\n${t("response")}: ${test?.response?.response}`
                                                                : ""
                                                            )
                                                            + (
                                                                test?.response?.sections ?
                                                                `\n\n${t("sections")}: ${test?.response?.sections}`
                                                                : ""
                                                            )}
                                                        </td>
                                                        <td style={{
                                                            padding: "16px 20px",
                                                            textAlign: "center",
                                                            verticalAlign: "top",
                                                            color: "var(--card-text)",
                                                            whiteSpace: "nowrap"
                                                        }}>
                                                            {test.response_time ? parseFloat(test.response_time).toFixed(3) : t("na")}
                                                        </td>
                                                        <td style={{
                                                            padding: "16px 20px",
                                                            textAlign: "center",
                                                            verticalAlign: "top",
                                                            whiteSpace: "nowrap"
                                                        }}>
                                                            <span style={{
                                                                background: getRiskLevelColor(test.risk_level),
                                                                color: "white",
                                                                padding: "4px 12px",
                                                                borderRadius: "4px",
                                                                fontSize: "12px",
                                                                fontWeight: "bold",
                                                                display: "inline-block"
                                                            }}>
                                                                {levelDisplayNames[test?.risk_level] || t("na")}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FailedTestDetailsTab;
